/**
 * Display date formatting shared by clients.
 *
 * Storage format is always YYYY-MM-DD. This module maps that to a user-facing
 * format (ISO, DMY, MDY, YMD) or auto-detects one from a locale via Intl.
 */

/** @typedef {'auto' | 'iso' | 'ymd' | 'dmy' | 'mdy'} DateFormat */

/** @type {ReadonlyArray<DateFormat>} */
export const DATE_FORMATS = Object.freeze(['auto', 'iso', 'ymd', 'dmy', 'mdy']);

/**
 * Format a YYYY-MM-DD string into the given display format.
 * Non-date or malformed input is returned unchanged.
 * @param {string} isoDate
 * @param {string} format
 * @returns {string}
 */
export function formatDate(isoDate, format) {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
	if (!match) { return isoDate; }
	const [, y, m, d] = match;
	switch (format) {
		case 'ymd': return `${y}/${m}/${d}`;
		case 'dmy': return `${d}/${m}/${y}`;
		case 'mdy': return `${m}/${d}/${y}`;
		default: return isoDate;
	}
}

/**
 * Resolve a date format setting to a concrete format. 'auto' detects the
 * ordering from the given locale (via Intl part order, ignoring era/literals
 * so calendars like Buddhist or Hijri still work). When no locale or an
 * unresolvable one is provided, defaults to 'ymd'.
 * @param {string} setting
 * @param {string} [locale]
 * @returns {string}
 */
export function resolveDateFormat(setting, locale) {
	if (setting !== 'auto') { return setting; }
	if (!locale || isUnresolvableLocale(locale)) { return 'ymd'; }
	try {
		const parts = new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit' })
			.formatToParts(new Date(2000, 0, 2));
		const order = parts
			.filter((p) => p.type === 'year' || p.type === 'month' || p.type === 'day')
			.map((p) => p.type);
		if (order.length !== 3) { return 'ymd'; }
		if (order[0] === 'year') { return 'ymd'; }
		if (order[0] === 'day') { return 'dmy'; }
		return 'mdy';
	} catch {
		return 'ymd';
	}
}

/**
 * A locale is unresolvable when Intl falls back to the runtime default
 * (meaning the requested tag was not recognised) or when it throws.
 * @param {string} locale
 * @returns {boolean}
 */
function isUnresolvableLocale(locale) {
	try {
		const def = new Intl.DateTimeFormat().resolvedOptions().locale;
		const resolved = new Intl.DateTimeFormat(locale).resolvedOptions().locale;
		return resolved === def && locale !== def;
	} catch {
		return true;
	}
}

/**
 * Resolve a column's display string for a row. Honors an explicit
 * `getDisplayValue`, formats `type: 'date'` columns, and otherwise falls
 * back to `getValue`. Shared by web renderers so date display stays in one
 * place.
 * @param {{ type?: string, getValue: (row: Record<string, unknown>) => unknown, getDisplayValue?: (row: Record<string, unknown>) => unknown }} col
 * @param {Record<string, unknown>} row
 * @param {string} dateFormat
 * @returns {unknown}
 */
export function columnDisplay(col, row, dateFormat) {
	if (col.getDisplayValue) {
		const display = col.getDisplayValue(row);
		if (display !== null && display !== undefined) { return display; }
	}
	if (col.type === 'date') { return formatDate(String(col.getValue(row) ?? ''), dateFormat); }
	return col.getValue(row);
}
